
import React, { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { toast } from '@/hooks/use-toast';
import { ProductPricing } from './ProductPricing';
import { ProductSizeSelector } from './ProductSizeSelector';
import { ProductColorSelector } from './ProductColorSelector';
import { ProductQuantityControls } from './ProductQuantityControls';
import { ProductActions } from './ProductActions';
import { EnhancedProductVariantSelector } from './EnhancedProductVariantSelector';
import { ProductImageGallery } from './ProductImageGallery';
import { useProductVariants } from '@/hooks/useProductVariants';
import { useProductAttributes } from '@/hooks/useProductAttributes';
import { useExpressCheckout } from '@/hooks/useExpressCheckout';
import { DependencyRule, DbProduct } from '@/hooks/useProduct';

interface ProductInfoProps {
  id: string;
  name: string;
  price: number;
  description?: string;
  glanceInfo?: string[] | null;
  stock: number;
  availableSizes?: any[];
  availableColors?: any[];
  defaultSize?: string;
  defaultColor?: string;
  onColorChange?: (color: any) => void;
  onVariantChange?: (variant: any) => void;
  discountPrice?: number;
  isDiscounted?: boolean;
  selectedColor?: any;
  imageUrls?: string[];
  dependencyRules?: DependencyRule[];
  isMobileLayout?: boolean;
  product?: DbProduct;
  mobileRenderMode?: 'header' | 'selectors' | 'all';
  // Pre-loaded variants from parent to avoid double-loading
  preloadedVariants?: any[];
  preloadedSelectedVariant?: any;
  // Shared quantity state for mobile (lifted to parent so header and selectors share it)
  sharedQuantity?: number;
  onSharedQuantityChange?: (quantity: number) => void;
}

export const ProductInfo = ({ 
  id, 
  name, 
  price, 
  description, 
  glanceInfo, 
  stock,
  availableSizes = [],
  availableColors = [],
  defaultSize,
  defaultColor,
  onColorChange,
  onVariantChange,
  discountPrice,
  isDiscounted,
  selectedColor,
  imageUrls = [],
  dependencyRules = [],
  isMobileLayout = false,
  product,
  mobileRenderMode = 'all',
  preloadedVariants,
  preloadedSelectedVariant,
  sharedQuantity,
  onSharedQuantityChange
}: ProductInfoProps) => {
  const { addToCart } = useCart();
  const { handleBuyNow: expressCheckout, isLoading: expressLoading } = useExpressCheckout();
  const [selectedSize, setSelectedSize] = useState<string>(defaultSize || '');
  const [localQuantity, setLocalQuantity] = useState(1);
  // Use shared quantity if provided (for mobile), otherwise use local state
  const quantity = sharedQuantity !== undefined ? sharedQuantity : localQuantity;
  const setQuantity = onSharedQuantityChange || setLocalQuantity;
  const [showColorError, setShowColorError] = useState(false);

  // Use preloaded variants if provided, otherwise load them (for backward compatibility)
  const {
    variants: loadedVariants,
    selectedVariant: loadedSelectedVariant,
    setSelectedVariant,
    isLoading: variantsLoading,
    error: variantsError,
  } = useProductVariants(preloadedVariants ? '' : id); // Skip loading if preloaded

  // Use preloaded data if available
  const variants = preloadedVariants || loadedVariants;
  const selectedVariant = preloadedSelectedVariant || loadedSelectedVariant;

  const { attributes } = useProductAttributes(id);

  const handleVariantChange = (variant: any) => {
    // Only use setSelectedVariant if not using preloaded data
    if (!preloadedVariants) {
      setSelectedVariant(variant);
    }
    if (onVariantChange) {
      onVariantChange(variant);
    }
  };

  const enabledSizes = availableSizes.filter(size => size.enabled);
  const enabledColors = availableColors.filter(color => color.enabled);

  // Determine which system to use - variants or legacy colors/sizes
  const hasVariants = variants.length > 0;
  
  // Skip loading states when using preloaded data
  const isLoading = !preloadedVariants && variantsLoading;

  const handleAddToCart = () => {
    const currentStock = selectedVariant?.stock || 0;
    
    if (currentStock <= 0) {
      toast({
        title: "Out of Stock",
        description: "This item is currently out of stock.",
        variant: "destructive",
      });
      return;
    }

    // Check if pot/size selection is required but not made
    if (hasVariants && !selectedVariant) {
      toast({
        title: "Selection Required",
        description: "Please select a pot type and size.",
        variant: "destructive",
      });
      return;
    }

    // Check if pot color is required but not selected (legacy system)
    if (!hasVariants && enabledColors.length > 0 && !selectedColor) {
      setShowColorError(true);
      return;
    }

    // Clear any existing error
    setShowColorError(false);

    const finalPrice = hasVariants && selectedVariant 
      ? (selectedVariant.discount_price || selectedVariant.price)
      : calculateFinalPrice();
    
    const resolvedImageUrls = selectedVariant?.image_url
      ? [selectedVariant.image_url]
      : (imageUrls && imageUrls.length > 0 ? imageUrls : ['/placeholder.svg']);
    const productForCart = {
      id,
      name,
      price: finalPrice,
      image_urls: resolvedImageUrls,
      category: null,
      currency: 'INR',
      description,
      stock: currentStock,
      is_published: true,
      glance_info: glanceInfo,
      is_discounted: isDiscounted,
      discount_price: discountPrice,
      include_in_deals: false,
      available_colors: availableColors
    };
    
    // Determine cart item details based on system used
    const colorInfo = !hasVariants && selectedColor 
      ? { name: selectedColor.name, color: selectedColor.hexColor } 
      : undefined;
    
    const sizeInfo = hasVariants && selectedVariant
      ? selectedVariant.size 
      : selectedSize || undefined;

    // For variants, include pot type and color in the cart item
    const variantInfo = hasVariants && selectedVariant ? {
      pot_type: selectedVariant.pot_type,
      pot_color: selectedVariant.pot_color,
      size: selectedVariant.size
    } : undefined;

    // Fix: Use correct function signature - addToCart expects (product, selectedPotColor?, selectedSize?, variantInfo?, silent?, quantity?)
    addToCart(productForCart, colorInfo, sizeInfo, variantInfo, undefined, quantity);

    const itemDescription = hasVariants && selectedVariant
      ? `${name} (${selectedVariant.pot_type}, ${selectedVariant.size}, ${selectedVariant.pot_color})`
      : name;

    toast({
      title: "Added to Cart",
      description: `${itemDescription} has been added to your cart.`,
    });
  };

  const handleColorChange = (colorId: string) => {
    const colorObj = enabledColors.find(color => color.id === colorId);
    if (colorObj && onColorChange) {
      setShowColorError(false);
      onColorChange(colorObj);
    }
  };

  // Handle Buy Now (express checkout)
  const handleBuyNow = (type: 'razorpay' | 'shiprocket') => {
    const currentStock = selectedVariant?.stock || 0;
    if (currentStock <= 0) {
      toast({
        title: "Out of Stock",
        description: "This item is currently out of stock.",
        variant: "destructive",
      });
      return;
    }

    // Check variant selection
    if (hasVariants && !selectedVariant) {
      toast({
        title: "Selection Required",
        description: "Please select a pot type and size.",
        variant: "destructive",
      });
      return;
    }

    const finalPrice = hasVariants && selectedVariant 
      ? (selectedVariant.discount_price || selectedVariant.price)
      : calculateFinalPrice();

    // Build product data for express checkout
    const productData = product ? {
      ...product,
      image_urls: imageUrls
    } : {
      id,
      name,
      price: finalPrice,
      image_urls: imageUrls,
      stock: currentStock,
    };

    // Call express checkout directly
    expressCheckout(
      {
        product: productData as any,
        price: finalPrice,
        quantity,
        selectedPotType: selectedVariant?.pot_type,
        selectedPotColor: selectedVariant?.pot_color,
        selectedSize: selectedVariant?.size,
      },
      type
    );
  };

  // Calculate dynamic price based on selected size and pot color (legacy system)
  const calculateFinalPrice = () => {
    let basePrice = isDiscounted && discountPrice ? discountPrice : price;
    
    // Add size modifier
    const selectedSizeObj = enabledSizes.find(size => size.id === selectedSize);
    if (selectedSizeObj?.priceModifier) {
      basePrice += selectedSizeObj.priceModifier;
    }
    
    // Add pot color modifier
    if (selectedColor?.priceModifier) {
      basePrice += selectedColor.priceModifier;
    }
    
    return basePrice;
  };

  // Determine final display price and original price
  const displayPrice = hasVariants && selectedVariant 
    ? selectedVariant.price 
    : calculateFinalPrice();
  
  const originalPrice = hasVariants 
    ? null // Variants have their own pricing
    : (isDiscounted && discountPrice ? price : null);

  const currentStock = selectedVariant?.stock || 0;

  if (isMobileLayout) {
    if (mobileRenderMode === 'header') {
      // Only render sticky header
      return (
        <div className="bg-background border-b border-border py-3 px-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-foreground mb-1">{name}</h1>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-primary">₹{displayPrice.toFixed(2)}</span>
                {originalPrice && isDiscounted && (
                  <span className="text-sm text-muted-foreground line-through">₹{originalPrice.toFixed(2)}</span>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <ProductActions 
                onAddToCart={handleAddToCart}
                stock={currentStock}
                compact={true}
              />
            </div>
          </div>
        </div>
      );
    } else if (mobileRenderMode === 'selectors') {
      // Render variant selectors + quantity + actions (including express checkout)
      return (
        <div className="space-y-4 px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center p-6">
              <div className="text-muted-foreground text-sm">Loading variants...</div>
            </div>
          ) : variantsError ? (
            <div className="text-center p-4 text-destructive text-sm">
              Error loading variants: {variantsError}
            </div>
          ) : hasVariants ? (
            <EnhancedProductVariantSelector
              variants={variants}
              selectedVariant={selectedVariant}
              onVariantChange={handleVariantChange}
              attributes={attributes}
              dependencyRules={dependencyRules}
              isBundle={product?.is_bundle || false}
            />
          ) : (
            <>
              <ProductSizeSelector 
                enabledSizes={enabledSizes}
                selectedSize={selectedSize}
                onSizeChange={setSelectedSize}
              />

              <ProductColorSelector 
                enabledColors={enabledColors}
                selectedColor={selectedColor}
                onColorChange={handleColorChange}
                showColorError={showColorError}
              />
            </>
          )}
          
          <ProductQuantityControls 
            quantity={quantity}
            stock={currentStock}
            onQuantityChange={setQuantity}
          />

          <ProductActions 
            onAddToCart={handleAddToCart}
            onBuyNow={handleBuyNow}
            stock={currentStock}
          />
        </div>
      );
    }
    
    // Default: render everything (shouldn't reach here with new structure)
    return (
      <>
        {/* Sticky Header with Name, Price, and Add to Cart - ONLY */}
        <div className="sticky top-[60px] z-20 bg-background border-b border-border pb-3 pt-3 px-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-foreground mb-2">{name}</h1>
              <ProductPricing variant={hasVariants ? selectedVariant : { price: displayPrice, discount_price: originalPrice ? discountPrice : null, is_discounted: isDiscounted }} />
            </div>
            <ProductActions 
              onAddToCart={handleAddToCart}
              stock={currentStock}
              compact={true}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6 -mt-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{name}</h1>
        <ProductPricing variant={hasVariants ? selectedVariant : { price: displayPrice, discount_price: originalPrice ? discountPrice : null, is_discounted: isDiscounted }} />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">Loading variants...</div>
        </div>
      ) : variantsError ? (
        <div className="text-center p-4 text-destructive">
          Error loading variants: {variantsError}
        </div>
      ) : hasVariants ? (
        <EnhancedProductVariantSelector
          variants={variants}
          selectedVariant={selectedVariant}
          onVariantChange={handleVariantChange}
          attributes={attributes}
          dependencyRules={dependencyRules}
          isBundle={product?.is_bundle || false}
        />
      ) : (
        <>
          <ProductSizeSelector 
            enabledSizes={enabledSizes}
            selectedSize={selectedSize}
            onSizeChange={setSelectedSize}
          />

          <ProductColorSelector 
            enabledColors={enabledColors}
            selectedColor={selectedColor}
            onColorChange={handleColorChange}
            showColorError={showColorError}
          />
        </>
      )}

      <ProductQuantityControls 
        quantity={quantity}
        stock={currentStock}
        onQuantityChange={setQuantity}
      />

      <ProductActions 
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
        stock={currentStock}
      />
    </div>
  );
};
